/**
 * TranscriptEvidencePane
 *
 * Evidence viewer that pairs an audio waveform with a searchable transcript.
 * Supports jumping between search matches, syncing scroll to the focused
 * segment from the left pane, and basic playback controls via WaveSurfer.
 */
import { PauseIcon, PlayIcon } from '@heroicons/react/24/solid';
import clsx from 'clsx';
import { useEffect, useMemo, useRef, useState } from 'react';
import WaveSurfer from 'wavesurfer.js';

import AudioWaveform from '@/components/audio-conversation/AudioWaveform';
import { ConversationAudioControls } from '@/components/audio-conversation-preview/ConversationAudioControls';
import { PreviewHeader } from '@/components/audio-conversation-preview/PreviewHeader';
import { SearchBar } from '@/components/audio-conversation-preview/SearchBar';
import { TranscriptList } from '@/components/audio-conversation-preview/TranscriptList';
import { TranscriptionSegment } from '@/services/types/transcript-types';

type MinimalConversation = {
  id: string;
  name?: string | null;
  duration?: string | null;
  start_timestamp: string;
  is_inbound: boolean;
  /** Full audio URL (presigned) or any accessible media URL */
  audio_url?: string | null;
  /** S3 audio path; use existing audio loader infra when present */
  audio_path?: string | null;
};

/** Props for `TranscriptEvidencePane`. */
interface TranscriptEvidencePaneProps {
  /** Minimal conversation data (id, name, timestamps, audio url). */
  conversation: MinimalConversation;
  /** Transcript segments to render/search. */
  segments: TranscriptionSegment[];
  /** If provided, the pane will scroll/highlight this segment id. */
  focusSegmentId?: string | null;
}

export const TranscriptEvidencePane = ({
  conversation,
  segments,
  focusSegmentId,
}: TranscriptEvidencePaneProps) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [matchIds, setMatchIds] = useState<string[]>([]);
  const [matchRanges, setMatchRanges] = useState<
    Record<string, Array<[number, number]>>
  >({});
  const [activeMatchIndex, setActiveMatchIndex] = useState(0);

  const segmentRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const waveSurferRef = useRef<WaveSurfer | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [playingSegmentId, setPlayingSegmentId] = useState<string | null>(null);

  // Build a tiny index for search (simple includes to keep deps minimal)
  const lowercaseTexts = useMemo(() => {
    return Object.fromEntries(
      segments.map((s) => [s.id, s.text.toLowerCase()]),
    ) as Record<string, string>;
  }, [segments]);

  useEffect(() => {
    if (!searchQuery.trim()) {
      setMatchIds([]);
      setMatchRanges({});
      setActiveMatchIndex(0);
      return;
    }
    const q = searchQuery.toLowerCase();
    const ids: string[] = [];
    const ranges: Record<string, Array<[number, number]>> = {};
    segments.forEach((seg) => {
      const text = lowercaseTexts[seg.id];
      if (!text) return;
      let from = 0;
      while (true) {
        const idx = text.indexOf(q, from);
        if (idx === -1) break;
        if (!ranges[seg.id]) ranges[seg.id] = [];
        ranges[seg.id].push([idx, idx + q.length - 1]);
        from = idx + q.length;
      }
      if (ranges[seg.id]?.length) ids.push(seg.id);
    });
    setMatchIds(ids);
    setMatchRanges(ranges);
    setActiveMatchIndex(ids.length ? 0 : 0);
  }, [searchQuery, segments, lowercaseTexts]);

  // Scroll to active search match within local scroll container
  useEffect(() => {
    if (!matchIds.length) return;
    const activeId = matchIds[activeMatchIndex];
    if (!activeId) return;
    const el = segmentRefs.current[activeId];
    if (el) {
      el.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'nearest',
      });
    }
  }, [activeMatchIndex, matchIds]);

  // Scroll to externally focused segment (from left pane) within local scroll container
  useEffect(() => {
    if (!focusSegmentId) return;
    const el = segmentRefs.current[focusSegmentId];
    if (el) {
      el.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'nearest',
      });
    }
    setPlayingSegmentId(focusSegmentId);
  }, [focusSegmentId]);

  const handleTogglePlayPause = () => {
    const ws = waveSurferRef.current;
    if (!ws) return;
    ws.playPause();
  };

  const gotoPreviousMatch = () => {
    setActiveMatchIndex((cur) => (cur <= 0 ? matchIds.length - 1 : cur - 1));
  };
  const gotoNextMatch = () => {
    setActiveMatchIndex((cur) => (cur >= matchIds.length - 1 ? 0 : cur + 1));
  };

  // Determine current playing segment based on time
  useEffect(() => {
    if (!isPlaying || !segments.length) return;
    let current: string | null = null;
    for (let i = segments.length - 1; i >= 0; i -= 1) {
      if (segments[i].start_time <= currentTime) {
        current = segments[i].id;
        break;
      }
    }
    if (current !== playingSegmentId) setPlayingSegmentId(current);
  }, [currentTime, isPlaying, segments, playingSegmentId]);

  return (
    <div className="flex flex-col h-full bg-white rounded-lg border border-gray-100 shadow-sm overflow-hidden">
      <PreviewHeader
        conversation={{
          name: conversation.name || 'Audio Conversation',
          duration: conversation.duration || null,
          start_timestamp: conversation.start_timestamp,
          is_inbound: conversation.is_inbound,
        }}
      />

      <div className="flex-1 overflow-hidden">
        <div className="h-full flex flex-col">
          <SearchBar
            query={searchQuery}
            onChange={setSearchQuery}
            onNext={gotoNextMatch}
            onPrev={gotoPreviousMatch}
            hasResults={segments.length > 0}
          />

          {conversation.audio_path ? (
            <ConversationAudioControls
              conversation={{
                id: conversation.id,
                audio_path: conversation.audio_path || null,
                duration: conversation.duration || undefined,
              }}
              onReady={(ws) => {
                waveSurferRef.current = ws;
                ws.on('audioprocess', (t: number) => setCurrentTime(t));
                ws.on('seeking', (p: number) => {
                  const dur = ws.getDuration();
                  if (dur) setCurrentTime(p * dur);
                });
                ws.on('play', () => setIsPlaying(true));
                ws.on('pause', () => setIsPlaying(false));
                ws.on('finish', () => setIsPlaying(false));
              }}
              onTimeUpdate={(t) => setCurrentTime(t)}
            />
          ) : (
            conversation.audio_url && (
              <div className="border-b border-gray-50 px-5 py-3 bg-white flex items-center gap-2">
                <button
                  type="button"
                  onClick={handleTogglePlayPause}
                  className={clsx(
                    'p-1 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded transition-colors',
                  )}
                  aria-label={isPlaying ? 'Pause' : 'Play'}
                >
                  {isPlaying ? (
                    <PauseIcon className="h-4 w-4" />
                  ) : (
                    <PlayIcon className="h-4 w-4" />
                  )}
                </button>
                <AudioWaveform
                  audioUrl={conversation.audio_url}
                  onReady={(ws) => {
                    waveSurferRef.current = ws;
                    ws.on('audioprocess', (t: number) => setCurrentTime(t));
                    ws.on('seeking', (p: number) => {
                      const dur = ws.getDuration();
                      if (dur) setCurrentTime(p * dur);
                    });
                    ws.on('play', () => setIsPlaying(true));
                    ws.on('pause', () => setIsPlaying(false));
                    ws.on('finish', () => setIsPlaying(false));
                  }}
                  onPlay={() => setIsPlaying(true)}
                  onPause={() => setIsPlaying(false)}
                  isPlaying={isPlaying}
                />
              </div>
            )
          )}

          <div className="flex-1 overflow-y-auto">
            {segments.length === 0 && (
              <div className="flex items-center justify-center h-32">
                <div className="text-gray-400 text-sm">
                  No transcript segments available
                </div>
              </div>
            )}
            {segments.length > 0 && (
              <TranscriptList
                segments={segments}
                searchQuery={searchQuery}
                matchIds={matchIds}
                matchRanges={matchRanges}
                playingSegmentId={playingSegmentId}
                audioIsPlaying={isPlaying}
                onSegmentInteract={(id) => {
                  const ws = waveSurferRef.current;
                  if (!ws) return;
                  const dur = ws.getDuration();
                  const seg = segments.find((s) => s.id === id);
                  if (!seg || !dur) return;
                  ws.seekTo(seg.start_time / dur);
                }}
                segmentRefs={segmentRefs}
              />
            )}
          </div>

          {searchQuery && matchIds.length > 0 && (
            <div className="px-6 py-3 border-t border-gray-50 bg-gray-25 flex justify-between items-center">
              <span className="text-xs text-gray-500 font-medium">
                {activeMatchIndex + 1} of {matchIds.length} matches
              </span>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  className="px-3 py-1.5 text-xs font-medium bg-white hover:bg-gray-50 border border-gray-200 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  onClick={gotoPreviousMatch}
                  disabled={matchIds.length <= 1}
                >
                  Previous
                </button>
                <button
                  type="button"
                  className="px-3 py-1.5 text-xs font-medium bg-white hover:bg-gray-50 border border-gray-200 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  onClick={gotoNextMatch}
                  disabled={matchIds.length <= 1}
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
